import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import { getPartner } from "../../redux/actions/indexActions";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineEye, AiOutlineSearch } from "react-icons/ai";
import { FaEllipsisV } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import { MdAdd } from "react-icons/md";
import { Input } from "reactstrap";
import Pagination from "../../Components/Paginate";
import { CSVLink } from "react-csv";
import { BiSolidFileExport } from "react-icons/bi";
import moment from "moment";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const TIME_FILTER_CUSTOM = "custom";
const TIME_OPEN_CUSTOM_MODAL = "__open_custom__";

const COLORS = {
  pending: "#F59E0B",
  info: "#8B5CF6",
  approved: "#10B981",
  rejected: "#EF4444",
};

const INFO_REASON_HINTS = ["Follow Up Required", "Waiting for Documents", "Under Review"];

const FALLBACK_CATEGORIES = ["Organization", "Business", "Merchant"];

const DEFAULT_TIME_FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
];

const pickFirstArray = (obj, keys) => {
  if (!obj || typeof obj !== "object") return [];
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v) && v.length) return v;
  }
  return [];
};

const normalizeCategoryOptionRow = (item) => {
  if (item == null) return null;
  if (typeof item === "string") return { value: item, label: item };
  const value = item.value ?? item.category ?? item.name ?? item.title ?? item.id ?? item.label;
  const label = item.label ?? item.name ?? item.category ?? item.title ?? (value != null ? String(value) : "");
  if (value == null || value === "") return null;
  return { value: String(value), label: label || String(value) };
};

const categoryBadgeStyle = (cat) => {
  const c = (cat || "").toLowerCase();
  if (c.includes("organ")) return { background: "#EFF6FF", color: "#2563EB" };
  if (c.includes("business")) return { background: "#F5F3FF", color: "#7C3AED" };
  if (c.includes("merchant")) return { background: "#ECFDF5", color: "#059669" };
  return { background: "#F3F4F6", color: "#4B5563" };
};

const applicationStatus = (item) => {
  if (item?.isVerified === 1) return { key: "approved", label: "Approved", color: COLORS.approved };
  if (item?.isVerified === 2) return { key: "rejected", label: "Rejected", color: COLORS.rejected };
  if (
    item?.isVerified === 0 &&
    item?.reason &&
    INFO_REASON_HINTS.some((h) => item.reason.includes(h))
  ) {
    return { key: "info", label: "Info Requested", color: COLORS.info };
  }
  return { key: "pending", label: "Pending", color: COLORS.pending };
};

const normalizeStateDropdownPayload = (data) => {
  const raw =
    data?.stateLists ??
    data?.stateDropdownList ??
    data?.states ??
    data?.data ??
    (Array.isArray(data) ? data : []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const id = row?.id ?? row?.state_id ?? row?.stateId;
      const state_name = row?.state_name ?? row?.name ?? row?.label ?? row?.title;
      return { id, state_name };
    })
    .filter((row) => row.state_name);
};

const stateOptionValue = (s) => (s.id != null && s.id !== "" ? String(s.id) : s.state_name);

const normalizePartnerFilterMetaFromResponse = (data) => {
  const catRaw = pickFirstArray(data, [
    "allCategories",
    "categories",
    "categoryList",
    "category_lists",
    "filterCategories",
    "CategoryList",
    "partnerCategories",
  ]);
  const catMap = new Map();
  catRaw.forEach((row) => {
    const c = normalizeCategoryOptionRow(row);
    if (c) catMap.set(c.value, c);
  });
  let categories = Array.from(catMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  if (!categories.length) {
    categories = FALLBACK_CATEGORIES.map((c) => ({ value: c, label: c }));
  }

  const stateRaw = pickFirstArray(data, [
    "allStates",
    "stateList",
    "states",
    "filterStates",
    "partnerStates",
    "StateList",
    "state_lists",
  ]);
  const states = stateRaw.length ? normalizeStateDropdownPayload({ stateLists: stateRaw }) : [];

  const timeRaw = pickFirstArray(data, [
    "timeFilters",
    "dateFilters",
    "periods",
    "time_ranges",
    "TimeFilters",
    "date_ranges",
  ]);
  let timeOptions = timeRaw
    .map((item) => {
      if (typeof item === "string") return { value: item, label: item };
      const value = item.value ?? item.key ?? item.id ?? item.slug ?? item.code;
      const label = item.label ?? item.name ?? item.title ?? (value != null ? String(value) : "");
      if (value == null || value === "") return null;
      return { value: String(value), label: label || String(value) };
    })
    .filter(Boolean);
  if (!timeOptions.length) {
    timeOptions = [...DEFAULT_TIME_FILTER_OPTIONS];
  }

  return { categories, states, timeOptions };
};

const submittedDisplay = (createdAt) => {
  if (!createdAt) return { line: "—", badge: null };
  const m = moment(createdAt);
  const hours = moment().diff(m, "hours");
  const days = moment().diff(m, "days");
  let badge = null;
  if (hours >= 0 && hours < 24) badge = { text: `${Math.max(1, hours)}h`, bg: "#FFFBEB", color: COLORS.pending };
  else if (days >= 1 && days < 14) badge = { text: `${days}d`, bg: "#FEF2F2", color: COLORS.rejected };
  const line = m.calendar(null, {
    sameDay: "[Today] h:mm A",
    lastDay: "[Yesterday] h:mm A",
    sameElse: "DD MMM YYYY h:mm A",
  });
  return { line, badge };
};

const PartnerIndex = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    AuthReducer: { auth },
    indexReducer: { PartnerLists, partnerCount, statusCounts },
    PermissionReducer: { allowedPermissions },
  } = useSelector((state) => state);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [paginate, setPaginate] = useState(50);
  const [loading, setLoading] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});
  const [verificationModal, setVerificationModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(1);
  const [disposition, setDisposition] = useState("");
  const [verificationReason, setVerificationReason] = useState("");
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [timeFilter, setTimeFilter] = useState("");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [dateRangeModal, setDateRangeModal] = useState(false);
  const [calendarRangeValue, setCalendarRangeValue] = useState(null);

  const [partnerFilterMeta, setPartnerFilterMeta] = useState({
    categories: [],
    states: [],
    timeOptions: [...DEFAULT_TIME_FILTER_OPTIONS],
  });

  const partnerListFilters = useMemo(
    () => ({
      category: categoryFilter,
      state_id: stateFilter,
      time_filter: timeFilter === TIME_FILTER_CUSTOM ? TIME_FILTER_CUSTOM : timeFilter,
      date_from:
        timeFilter === TIME_FILTER_CUSTOM && customDateFrom ? customDateFrom : "",
      date_to: timeFilter === TIME_FILTER_CUSTOM && customDateTo ? customDateTo : "",
    }),
    [categoryFilter, stateFilter, timeFilter, customDateFrom, customDateTo]
  );

  const leadDispositions = [
    {
      value: 1,
      options: [
        { label: "Partner Qualified", value: "Partner Qualified" },
        { label: "Partner Converted", value: "Partner Converted" },
        { label: "Documents Verified", value: "Documents Verified" },
        { label: "Payment Completed", value: "Payment Completed" },
        { label: "Management Approved", value: "Management Approved" },
        { label: "Partner Approved", value: "Partner Approved" },
      ],
    },
    {
      value: 0,
      options: [
        { label: "Follow Up Required", value: "Follow Up Required" },
        { label: "Under Review", value: "Under Review" },
        { label: "Waiting for Documents", value: "Waiting for Documents" },
        { label: "Client Decision Pending", value: "Client Decision Pending" },
        { label: "Verification in Process", value: "Verification in Process" },
      ],
    },
    {
      value: 2,
      options: [
        { label: "Not Interested", value: "Not Interested" },
        { label: "Invalid Partner", value: "Invalid Partner" },
        { label: "Budget Issue", value: "Budget Issue" },
        { label: "Duplicate Partner", value: "Duplicate Partner" },
        { label: "Out of Service Area", value: "Out of Service Area" },
      ],
    },
  ];

  const dispositionOptionsByStatus = [
    { value: "", label: "Select Disposition" },
    ...(leadDispositions.find((d) => d.value === verificationStatus)?.options ?? []),
  ];

  let pageTitle = "Applications";
  let page_type = "";
  const isPartnerRegistrationSubTab =
    pathname.includes("approved-partners") ||
    pathname.includes("pending-partners") ||
    pathname.includes("rejected-partners");

  if (pathname.includes("approved-partners")) {
    pageTitle = "Approved applications";
    page_type = 1;
  } else if (pathname.includes("pending-partners")) {
    pageTitle = "Pending applications";
    page_type = 0;
  } else if (pathname.includes("rejected-partners")) {
    pageTitle = "Rejected applications";
    page_type = 2;
  }

  useEffect(() => {
    setPage(0);
    setPaginate(50);
  }, [page_type]);

  useEffect(() => {
    setLoading(true);
    dispatch(getPartner(auth?.token, paginate, page, search, sort, page_type, partnerListFilters));
    setLoading(false);
  }, [auth, paginate, page, search, sort, page_type, partnerListFilters, dispatch]);

  // useEffect(() => {
  //   if (!auth?.token) return;
  //   const headers = { Authorization: `Bearer ${auth.token}` };
  //   const base = `/api/admin/v1/partners/?limit=1&page=0&search=&sort=desc`;
  //   let cancelled = false;
  //   Promise.all([
  //     axios(`${base}&verified=0`, { headers }),
  //     axios(`${base}&verified=1`, { headers }),
  //     axios(`${base}&verified=2`, { headers }),
  //   ])
  //     .then(([r0, r1, r2]) => {
  //       if (cancelled) return;
  //       setStatusCounts({
  //         pending: r0.data?.partnerCount ?? 0,
  //         approved: r1.data?.partnerCount ?? 0,
  //         rejected: r2.data?.partnerCount ?? 0,
  //         infoRequested: null,
  //       });
  //     })
  //     .catch(() => {
  //       if (!cancelled) {
  //         setStatusCounts({ pending: null, approved: null, rejected: null, infoRequested: null });
  //       }
  //     });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [auth?.token]);

  useEffect(() => {
    if (!auth?.token) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${auth.token}` };
    const verifiedParam = page_type === "" || page_type === undefined ? "" : page_type;
    const metaUrl = `/api/admin/v1/partners/?limit=1&page=0&search=&sort=desc&verified=${verifiedParam}`;

    (async () => {
      try {
        const res = await axios.get(metaUrl, { headers });
        if (cancelled) return;
        let meta = normalizePartnerFilterMetaFromResponse(res.data);
        if (!meta.states.length) {
          try {
            const sres = await axios.get(`/api/admin/v1/state-dropdown-list`, { headers });
            if (cancelled) return;
            const states = normalizeStateDropdownPayload(sres.data);
            meta = { ...meta, states };
          } catch {
            /* optional fallback */
          }
        }
        setPartnerFilterMeta(meta);
      } catch {
        if (cancelled) return;
        let states = [];
        try {
          const sres = await axios.get(`/api/admin/v1/state-dropdown-list`, { headers });
          states = normalizeStateDropdownPayload(sres.data);
        } catch {
          /* ignore */
        }
        setPartnerFilterMeta({
          categories: FALLBACK_CATEGORIES.map((c) => ({ value: c, label: c })),
          states,
          timeOptions: [...DEFAULT_TIME_FILTER_OPTIONS],
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token, page_type]);

  const csvHead = [
    [
      "ref_id",
      "partner_name",
      "category",
      "category_type",
      "city",
      "state",
      "contact_name",
      "designation",
      "email",
      "phone",
      "submitted",
      "status",
    ],
  ];
  const csvData = (PartnerLists || []).map((cv) => {
    const st = applicationStatus(cv);
    const sub = submittedDisplay(cv?.createdAt);
    return [
      cv?.registration_id,
      cv?.businessName,
      cv?.category,
      cv?.sub_category,
      cv?.city,
      cv?.partnerState?.state_name,
      cv?.fullName,
      cv?.designation,
      cv?.email,
      cv?.phoneNumber,
      sub.line,
      st.label,
    ];
  });
  const exportData = csvHead.concat(csvData);

  const handleDel = (id) => {
    if (window.confirm("Are You Sure You Want To Delete This Partner")) {
      axios
        .delete(`/api/admin/v1/partner-delete/${id}`, {
          headers: {
            Authorization: `Bearer ${auth?.token}`,
          },
        })
        .then((res) => {
          toast.success(res.data.message);
          dispatch(
            getPartner(auth?.token, paginate, page, search, sort, page_type, partnerListFilters)
          );
        })
        .catch(() => toast.error("Somthing Went Wrong"));
    }
  };

  const handleStatusChange = async (partnerId, status) => {
    try {
      await axios.put(
        `/api/admin/v1/partner-status/${partnerId}`,
        { status },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      const statusText = status === 1 ? "Active" : status === 2 ? "Hold" : "Inactive";
      toast.success(`Partner status updated to ${statusText}`);
      dispatch(
        getPartner(auth?.token, paginate, page, search, sort, page_type, partnerListFilters)
      );
    } catch {
      toast.error("Failed to update partner status");
    }
  };

  const openVerificationModal = (item) => {
    setSelectedPartner(item);
    setVerificationStatus(item?.isVerified === 1 ? 1 : item?.isVerified === 2 ? 2 : 0);
    setDisposition("");
    setVerificationReason("");
    setVerificationModal(true);
  };

  const closeVerificationModal = () => {
    setVerificationModal(false);
    setSelectedPartner(null);
    setVerificationStatus(1);
    setDisposition("");
    setVerificationReason("");
  };

  const handleVerificationSubmit = async () => {
    if (!selectedPartner?.id) return;
    setVerificationSubmitting(true);
    try {
      await axios.post(
        "/api/admin/v1/verify-partner-registration",
        {
          partnerId: selectedPartner.id,
          status: verificationStatus,
          reason: disposition || "",
          remarks: verificationReason || "",
        },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      toast.success("Partner verification status updated successfully!");
      closeVerificationModal();
      dispatch(
        getPartner(auth?.token, paginate, page, search, sort, page_type, partnerListFilters)
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update verification status");
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const openCustomRangeModal = () => {
    if (customDateFrom && customDateTo) {
      const a = new Date(customDateFrom);
      const b = new Date(customDateTo);
      setCalendarRangeValue(a <= b ? [a, b] : [b, a]);
    } else {
      setCalendarRangeValue(null);
    }
    setDateRangeModal(true);
  };

  const closeDateRangeModal = () => {
    setDateRangeModal(false);
  };

  const applyCustomDateRange = () => {
    const v = calendarRangeValue;
    if (!Array.isArray(v) || v.length < 2 || !v[0] || !v[1]) {
      toast.warn("Select a start date and an end date on the calendar");
      return;
    }
    const d0 = v[0] instanceof Date ? v[0] : new Date(v[0]);
    const d1 = v[1] instanceof Date ? v[1] : new Date(v[1]);
    const [from, to] = d0 <= d1 ? [d0, d1] : [d1, d0];
    setCustomDateFrom(moment(from).format("YYYY-MM-DD"));
    setCustomDateTo(moment(to).format("YYYY-MM-DD"));
    setTimeFilter(TIME_FILTER_CUSTOM);
    setPage(0);
    setDateRangeModal(false);
  };

  const tabFilterLabel =
    pathname.includes("pending-partners") ? "Pending"
    : pathname.includes("approved-partners") ? "Approved"
    : pathname.includes("rejected-partners") ? "Rejected"
    : null;

  const tabFilterColor =
    tabFilterLabel === "Pending" ? COLORS.pending
    : tabFilterLabel === "Approved" ? COLORS.approved
    : tabFilterLabel === "Rejected" ? COLORS.rejected
    : "#6B7280";

  const startIdx = partnerCount === 0 ? 0 : page * paginate + 1;
  const endIdx = Math.min(page * paginate + (PartnerLists?.length || 0), partnerCount || 0);
  const hasListFilters = !!(categoryFilter || stateFilter || timeFilter);
  const rowIndexBase = page * paginate;

  const statCards = [
    {
      key: "pending",
      label: "Pending",
      value: statusCounts.pendingCount,
      color: COLORS.pending,
      to: "/pending-partners",
      tint: "#FFFBEB",
    },
    {
      key: "info",
      label: "Info Requested",
      value: statusCounts.infoCount,
      color: COLORS.info,
      to: "/pending-partners",
      tint: "#F5F3FF",
    },
    {
      key: "approved",
      label: "Approved",
      value: statusCounts.approvedCount,
      color: COLORS.approved,
      to: "/approved-partners",
      tint: "#ECFDF5",
    },
    {
      key: "rejected",
      label: "Rejected",
      value: statusCounts.rejectedCount,
      color: COLORS.rejected,
      to: "/rejected-partners",
      tint: "#FEF2F2",
    },
  ];

  return (
    <div
      className="partner-applications-page"
      style={{ background: "#F3F4F6", margin: "-12px -12px 0", padding: "20px 20px 32px", minHeight: "calc(100vh - 80px)" }}
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <h5 className="mb-0 fw-semibold" style={{ color: "#111827", fontSize: "1.25rem" }}>
          {pageTitle}
        </h5>
        {allowedPermissions?.some?.(
          (item) =>
            item?.permission?.moduleType === "PARTNERS" &&
            item?.permission?.permission === "create"
        ) && (
          <Link to="/add-partner" className="text-decoration-none">
            <Button
              className="d-inline-flex align-items-center gap-2 border-0 fw-medium"
              style={{ background: "#111827", color: "#fff", borderRadius: "8px", padding: "8px 16px" }}
            >
              <MdAdd size={20} />
              Add Partner
            </Button>
          </Link>
        )}
      </div>

      <div className="row g-3 mb-4">
        {statCards.map((c) => (
          <div key={c.key} className="col-6 col-lg-3">
            <Link to={c.to} className="text-decoration-none text-reset">
              <div
                className="h-100 rounded-3 border-0 shadow-sm"
                style={{
                  background: c.key === "pending" ? c.tint : "#fff",
                  borderTop: `3px solid ${c.color}`,
                  padding: "18px 20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  cursor: "pointer",
                  transition: "transform 0.15s ease",
                }}
              >
                <div className="fw-bold mb-1" style={{ fontSize: "1.75rem", color: "#111827" }}>
                  {c.value === null ? "—" : c.value}
                </div>
                <div className="d-flex align-items-center gap-2 small fw-medium" style={{ color: "#4B5563" }}>
                  <span style={{ color: c.color, fontSize: "1.1rem", lineHeight: 1 }}>●</span>
                  {c.label}
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div
        className="bg-white rounded-3 p-3 p-md-4"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "1px solid #E5E7EB" }}
      >
        <div className="row g-2 align-items-center mb-3">
          <div className="col-12 col-xl-4">
            <div className="position-relative">
              <AiOutlineSearch
                className="position-absolute text-muted"
                size={18}
                style={{ left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
              />
              <Input
                bsSize="sm"
                className="border"
                style={{ paddingLeft: 38, borderRadius: "8px", height: "40px" }}
                value={search}
                placeholder="Search all applications — name, ref ID"
                onChange={({ target: { value } }) => setSearch(value)}
              />
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <Input
              type="select"
              bsSize="sm"
              style={{ borderRadius: "8px", height: "40px" }}
              value={categoryFilter}
              onChange={(e) => {
                setPage(0);
                setCategoryFilter(e.target.value);
              }}
            >
              <option value="">All categories</option>
              {(partnerFilterMeta.categories || []).map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </Input>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <Input
              type="select"
              bsSize="sm"
              style={{ borderRadius: "8px", height: "40px" }}
              value={stateFilter}
              onChange={(e) => {
                setPage(0);
                setStateFilter(e.target.value);
              }}
            >
              <option value="">All states</option>
              {(partnerFilterMeta.states || []).map((s, idx) => {
                const val = stateOptionValue(s);
                return (
                  <option key={`${val}-${idx}`} value={val}>
                    {s.state_name}
                  </option>
                );
              })}
            </Input>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <Input
              type="select"
              bsSize="sm"
              style={{ borderRadius: "8px", height: "40px" }}
              value={timeFilter}
              onChange={(e) => {
                const v = e.target.value;
                if (v === TIME_OPEN_CUSTOM_MODAL) {
                  openCustomRangeModal();
                  return;
                }
                setPage(0);
                setTimeFilter(v);
                if (v !== TIME_FILTER_CUSTOM) {
                  setCustomDateFrom("");
                  setCustomDateTo("");
                }
              }}
            >
              <option value="">Any time</option>
              {(partnerFilterMeta.timeOptions || [])
                .filter(
                  (t) =>
                    t.value !== TIME_FILTER_CUSTOM && t.value !== TIME_OPEN_CUSTOM_MODAL
                )
                .map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              {timeFilter === TIME_FILTER_CUSTOM && customDateFrom && customDateTo ? (
                <option value={TIME_FILTER_CUSTOM}>
                  {moment(customDateFrom).format("MMM D")} – {moment(customDateTo).format("MMM D, YYYY")}
                </option>
              ) : null}
              <option value={TIME_OPEN_CUSTOM_MODAL}>Custom range…</option>
            </Input>
          </div>
          <div className="col-6 col-md-4 col-xl-2 d-flex justify-content-xl-end">
            {allowedPermissions?.some?.(
              (item) =>
                item?.permission?.moduleType === "PARTNERS" &&
                item?.permission?.permission === "export"
            ) && (
              <CSVLink
                filename="partner-applications.csv"
                className="btn btn-sm d-inline-flex align-items-center gap-2 text-decoration-none text-dark border w-100 justify-content-center"
                style={{ borderRadius: "8px", height: "40px", borderColor: "#E5E7EB", background: "#fff" }}
                data={exportData}
              >
                <BiSolidFileExport size={18} />
                Export CSV
              </CSVLink>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 pb-2 border-bottom" style={{ borderColor: "#E5E7EB" }}>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="text-secondary small">
              Showing <strong className="text-dark">{partnerCount || 0}</strong>{" "}
              {hasListFilters ? "applications (filtered)" : "applications"}
            </span>
            {tabFilterLabel && (
              <span
                className="d-inline-flex align-items-center gap-1 small fw-medium px-2 py-1 rounded-pill"
                style={{
                  background: `${tabFilterColor}18`,
                  color: tabFilterColor,
                  border: `1px solid ${tabFilterColor}40`,
                }}
              >
                {tabFilterLabel}
                <button
                  type="button"
                  className="btn btn-link p-0 m-0 lh-1 text-decoration-none"
                  style={{ color: tabFilterColor, fontSize: "14px" }}
                  aria-label="Clear filter"
                  onClick={() => navigate("/partners")}
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <span className="text-muted small d-none d-md-inline">
            Hover Contact Person to see phone & email · Click status to update · Click any row for details
          </span>
        </div>

        <div className="table-responsive">
          <Table borderless className="align-middle mb-0 partner-apps-table" style={{ fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                <th className="text-secondary fw-semibold text-uppercase small py-3" style={{ width: 48 }}>
                  #
                </th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Ref ID</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Partner name</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Category</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Category type</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Location</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Contact person (hover)</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Submitted</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3">Status</th>
                <th className="text-secondary fw-semibold text-uppercase small py-3 text-end" style={{ width: 56 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-5">
                    <Spinner />
                  </td>
                </tr>
              ) : (PartnerLists || []).length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-5">
                    No data found
                  </td>
                </tr>
              ) : (
                (PartnerLists || []).map((item, index) => {
                  const st = applicationStatus(item);
                  const sub = submittedDisplay(item?.createdAt);
                  const catSt = categoryBadgeStyle(item?.category);
                  const rowNum = index + 1 + rowIndexBase;
                  const tipId = `contact-${item.id}`;
                  return (
                    <tr
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => window.open(`/partner-application-details/${item.id}`, "_blank")}
                      onKeyDown={(e) => e.key === "Enter" && window.open(`/partner-application-details/${item.id}`, "_blank")}
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        cursor: "pointer",
                      }}
                    >
                      <td className="py-3 text-muted">{rowNum}</td>
                      <td className="py-3">
                        <span
                          className="d-inline-block px-2 py-1 rounded-2 small fw-medium"
                          style={{ background: "#F3F4F6", color: "#374151" }}
                        >
                          {item?.registration_id ?? "—"}
                        </span>
                      </td>
                      <td className="py-3 fw-semibold text-dark">{item?.businessName ?? "N/A"}</td>
                      <td className="py-3">
                        <span
                          className="d-inline-block px-2 py-1 rounded-2 small fw-medium"
                          style={catSt}
                        >
                          {item?.category ?? "N/A"}
                        </span>
                      </td>
                      <td className="py-3 text-secondary">{item?.sub_category ?? "—"}</td>
                      <td className="py-3">
                        <div className="fw-semibold text-dark">{item?.city ?? "—"}</div>
                        <div className="small text-muted">
                          {item?.partnerState ? item?.partnerState?.state_name : "—"}
                        </div>
                      </td>
                      <td className="py-3" onClick={(e) => e.stopPropagation()}>
                        <span id={tipId} className="d-block" style={{ cursor: "default" }}>
                          <span className="fw-semibold text-dark d-block">{item?.fullName ?? "—"}</span>
                          <span className="small text-muted">{item?.designation ?? "—"}</span>
                        </span>
                        <UncontrolledTooltip placement="top" target={tipId} innerClassName="text-start shadow">
                          <div className="small">
                            {item?.phoneNumber && <div>{item.phoneNumber}</div>}
                            {item?.email && <div>{item.email}</div>}
                            {!item?.phoneNumber && !item?.email && <span>No contact on file</span>}
                          </div>
                        </UncontrolledTooltip>
                      </td>
                      <td className="py-3">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="text-dark">{sub.line}</span>
                          {sub.badge && (
                            <span
                              className="small px-2 py-0 rounded-2 fw-medium"
                              style={{
                                background: sub.badge.bg,
                                color: sub.badge.color,
                                fontSize: "0.7rem",
                              }}
                            >
                              {sub.badge.text}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="py-3"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          openVerificationModal(item);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            openVerificationModal(item);
                          }
                        }}
                        title="Click to update verification status"
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            st.key === "approved"
                              ? "#D1FAE5"
                              : st.key === "rejected"
                                ? "#FEE2E2"
                                : st.key === "info"
                                  ? "#EDE9FE"
                                  : "#FEF3C7",
                          borderRadius: "8px",
                          outline: "none",
                        }}
                      >
                        <span className="d-inline-flex align-items-center gap-2 fw-medium px-2 py-1" style={{ color: st.color }}>
                          <span style={{ fontSize: "1rem", lineHeight: 1 }}>●</span>
                          {st.label}
                        </span>
                      </td>
                      <td className="py-3 text-end" onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                          isOpen={actionDropdowns[item.id] || false}
                          toggle={() =>
                            setActionDropdowns((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                          }
                        >
                          <DropdownToggle
                            className="btn btn-sm btn-light border rounded-2 px-2"
                            style={{ boxShadow: "none" }}
                          >
                            <FaEllipsisV className="text-secondary" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem onClick={() => window.open(`/partner-application-details/${item.id}`, "_blank")}>
                              <AiOutlineEye className="me-2" /> View details
                            </DropdownItem>
                            <DropdownItem onClick={() => openVerificationModal(item)}>
                              Update verification
                            </DropdownItem>
                            {!isPartnerRegistrationSubTab && (
                              <DropdownItem header className="small text-muted">
                                Account status
                              </DropdownItem>
                            )}
                            {!isPartnerRegistrationSubTab && (
                              <>
                                <DropdownItem onClick={() => handleStatusChange(item.id, 1)}>
                                  <span className="text-success">● Active</span>
                                </DropdownItem>
                                <DropdownItem onClick={() => handleStatusChange(item.id, 2)}>
                                  <span className="text-warning">● Hold</span>
                                </DropdownItem>
                                <DropdownItem onClick={() => handleStatusChange(item.id, 0)}>
                                  <span className="text-danger">● Inactive</span>
                                </DropdownItem>
                              </>
                            )}
                            {allowedPermissions?.some?.(
                              (perm) =>
                                perm?.permission?.moduleType === "PARTNERS" &&
                                perm?.permission?.permission === "edit"
                            ) && (
                              <DropdownItem onClick={() => window.open(`/edit-partner/${item.id}`, "_blank")}>
                                <AiOutlineEdit className="me-2" /> Edit partner
                              </DropdownItem>
                            )}
                            {allowedPermissions?.some?.(
                              (perm) =>
                                perm?.permission?.moduleType === "PARTNERS" &&
                                perm?.permission?.permission === "delete"
                            ) && (
                              <>
                                <DropdownItem divider />
                                <DropdownItem onClick={() => handleDel(item.id)} className="text-danger">
                                  <AiOutlineDelete className="me-2" /> Delete partner
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className="row align-items-center mt-3 pt-3 border-top g-3" style={{ borderColor: "#E5E7EB" }}>
          <div className="col-12 col-md-6">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="text-secondary small">
                Showing {partnerCount === 0 ? 0 : startIdx}–{endIdx} of {partnerCount || 0} applications
                {hasListFilters ? " (filtered)" : ""}
              </span>
              <Input
                type="select"
                id="paginate"
                bsSize="sm"
                className="w-auto"
                style={{ borderRadius: "8px", minWidth: "72px" }}
                value={paginate}
                onChange={(e) => {
                  setPaginate(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </Input>
              <span className="text-muted small">per page</span>
              <Input
                type="select"
                bsSize="sm"
                className="w-auto"
                style={{ borderRadius: "8px", minWidth: "120px" }}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                title="Sort by submitted date"
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </Input>
            </div>
          </div>
          <div className="col-12 col-md-6 d-flex justify-content-md-end">
            <Pagination
              pageCount={Math.ceil((partnerCount || 0) / paginate) || 1}
              currentPage={page}
              onChange={(e) => setPage(e.selected)}
              previousLabel="← Prev"
              nextLabel="Next →"
              style={{
                "--paginate-selected-bg": "#111827",
                "--paginate-selected-color": "#ffffff",
              }}
            />
          </div>
        </div>
      </div>

      <Modal isOpen={dateRangeModal} toggle={closeDateRangeModal} centered>
        <ModalHeader toggle={closeDateRangeModal}>Custom date range</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">Choose a start date, then an end date (same month or use arrows to change month).</p>
          <div
            className="d-flex justify-content-center overflow-auto py-2"
            style={{ maxWidth: "100%" }}
          >
            <Calendar
              className="border rounded-3 shadow-sm"
              selectRange
              onChange={setCalendarRangeValue}
              value={calendarRangeValue}
              maxDate={new Date()}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDateRangeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyCustomDateRange}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={verificationModal} toggle={closeVerificationModal} size="md">
        <ModalHeader toggle={closeVerificationModal}>
          Update Verification Status
          {selectedPartner && (
            <span className="ms-2 text-muted small">
              — {selectedPartner?.businessName || selectedPartner?.fullName}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="d-block mb-2 fw-semibold">Status</Label>
            <div className="d-flex flex-wrap gap-3">
              <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="verificationStatus"
                  checked={verificationStatus === 0}
                  onChange={() => {
                    setVerificationStatus(0);
                    setDisposition("");
                  }}
                />
                <span>Pending</span>
              </label>
              <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="verificationStatus"
                  checked={verificationStatus === 1}
                  onChange={() => {
                    setVerificationStatus(1);
                    setDisposition("");
                  }}
                />
                <span>Verified</span>
              </label>
              <label className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="verificationStatus"
                  checked={verificationStatus === 2}
                  onChange={() => {
                    setVerificationStatus(2);
                    setDisposition("");
                  }}
                />
                <span>Rejected</span>
              </label>
            </div>
          </div>
          <div className="mb-3">
            <Label for="disposition" className="d-block mb-2 fw-semibold">
              Disposition
            </Label>
            <Input
              id="disposition"
              type="select"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            >
              {dispositionOptionsByStatus.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </div>
          <div>
            <Label for="verificationReason" className="d-block mb-2 fw-semibold">
              Reason for status change
            </Label>
            <Input
              id="verificationReason"
              type="textarea"
              rows={3}
              value={verificationReason}
              onChange={(e) => setVerificationReason(e.target.value)}
              placeholder="Enter reason (optional)"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeVerificationModal} disabled={verificationSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleVerificationSubmit} disabled={verificationSubmitting}>
            {verificationSubmitting ? "Updating..." : "Update"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PartnerIndex;
